import { ReactNode } from 'react';

export type DesignButtonVariant = 'primary' | 'secondary' | 'ghost' | 'link';

export type DesignButtonSize = 'md' | 'sm';

export type DesignButtonProps = {
  onClickHandler?: () => void;
  children: ReactNode;
  className?: string | undefined;
  disabled?: boolean;
  /**
   * Design system look. Left out on purpose by the older pages: without it the
   * button keeps its legacy full-width classes.
   */
  variant?: DesignButtonVariant;
  size?: DesignButtonSize;
  type?: 'button' | 'submit';
  /** Required on icon-only buttons. */
  ariaLabel?: string;
  title?: string;
};

const variantClasses: Record<DesignButtonVariant, string> = {
  primary:
    'bg-darker-red text-white outline outline-1 outline-darker-red hover:outline-design-red',
  secondary:
    'outline outline-1 outline-light-black hover:outline-design-red dark:outline-white',
  ghost:
    'text-light-black hover:bg-light-gray dark:text-sublte-white dark:hover:bg-design-secondary-black',
  // Reads as a link inside a sentence, so it brings its own box: no outline, no
  // background, and no size class to fight with (see sizeClassesOf).
  link: 'h-auto p-0 text-bright-blue hover:underline',
};

const sizeClasses: Record<DesignButtonSize, string> = {
  md: 'h-11 px-4 text-base',
  sm: 'h-7 px-2 text-xs',
};

/**
 * `link` sizes itself from the text around it. Handing it a size class as well
 * would put `h-11` and `h-auto` on the same element, and which one wins is then
 * down to the order Tailwind emitted them in.
 */
const sizeClassesOf = (
  variant: DesignButtonVariant,
  size?: DesignButtonSize,
) => (variant === 'link' ? '' : sizeClasses[size ?? 'md']);

const variantBaseClasses =
  'inline-flex items-center justify-center gap-2 rounded-sm font-medium disabled:cursor-not-allowed disabled:opacity-50';

/**
 * Builds the class string for a Design System button-shaped element - the
 * `<button>` below, and `DesignLinkButton`'s `<a>`. Without a variant it falls
 * back to the legacy full-width classes the older pages still rely on.
 */
export const designButtonClasses = (
  variant: DesignButtonVariant | undefined,
  size: DesignButtonSize | undefined,
  className: string | undefined,
) =>
  variant
    ? [
        variantBaseClasses,
        sizeClassesOf(variant, size),
        variantClasses[variant],
        className,
      ]
        .filter((part) => part)
        .join(' ')
    : `mt-2 w-full rounded-sm p-3 outline outline-1 hover:outline-design-red disabled:cursor-not-allowed ${className}`;

const DesignButton = ({
  onClickHandler,
  className,
  children,
  disabled,
  variant,
  size,
  type,
  ariaLabel,
  title,
}: DesignButtonProps) => (
  <button
    type={type === 'submit' ? 'submit' : 'button'}
    disabled={disabled}
    aria-label={ariaLabel}
    title={title}
    className={designButtonClasses(variant, size, className)}
    onClick={onClickHandler}
  >
    {children}
  </button>
);

DesignButton.defaultProps = {
  className: '',
  onClickHandler: undefined,
  disabled: false,
  variant: undefined,
  size: 'md',
  type: 'button',
  ariaLabel: undefined,
  title: undefined,
};

export default DesignButton;
